import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import {
  Clock, User, Trophy, PenLine, FlaskConical, Calculator,
  Globe, Languages, Dumbbell, Home, Coffee,
} from "lucide-react";
import { api } from "@/lib/api";

const TYPE_STYLE = {
  tennis: { bg: "bg-emerald-50", border: "border-emerald-200", chip: "text-emerald-700", Icon: Trophy },
  writing: { bg: "bg-amber-50", border: "border-amber-200", chip: "text-amber-700", Icon: PenLine },
  science: { bg: "bg-sky-50", border: "border-sky-200", chip: "text-sky-700", Icon: FlaskConical },
  math: { bg: "bg-indigo-50", border: "border-indigo-200", chip: "text-indigo-700", Icon: Calculator },
  humanities: { bg: "bg-orange-50", border: "border-orange-200", chip: "text-orange-700", Icon: Globe },
  language: { bg: "bg-purple-50", border: "border-purple-200", chip: "text-purple-700", Icon: Languages },
  phe: { bg: "bg-rose-50", border: "border-rose-200", chip: "text-rose-700", Icon: Dumbbell },
  homeroom: { bg: "bg-slate-50", border: "border-slate-200", chip: "text-slate-600", Icon: Home },
  break: { bg: "bg-slate-50", border: "border-slate-100", chip: "text-slate-400", Icon: Coffee },
};

const fmt = (t) => {
  const [h, m] = t.split(":").map(Number);
  const ap = h >= 12 ? "PM" : "AM";
  const hh = h % 12 === 0 ? 12 : h % 12;
  return `${hh}:${String(m).padStart(2, "0")} ${ap}`;
};

export default function Timetable() {
  const [days, setDays] = useState([]);
  const todayName = new Date().toLocaleDateString("en-US", { weekday: "long" });

  useEffect(() => {
    api.get("/timetable").then((r) => setDays(r.data));
  }, []);

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5" data-testid="timetable-grid">
      {days.map((day, i) => {
        const isToday = day.day === todayName;
        return (
          <motion.div
            key={day.day}
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: i * 0.05 }}
            className={`rounded-2xl border bg-white p-5 ${isToday ? "border-indigo-400 ring-2 ring-indigo-100" : "border-slate-200"}`}
          >
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-heading font-bold text-lg text-slate-900">{day.day}</h3>
              {isToday && <span className="text-xs font-bold px-2.5 py-1 rounded-full bg-indigo-600 text-white">TODAY</span>}
            </div>

            {day.items.length === 0 ? (
              <p className="text-sm text-slate-400 py-6 text-center">No school — free day 🎉</p>
            ) : (
              <div className="space-y-2">
                {day.items.map((it, idx) => {
                  const s = TYPE_STYLE[it.type] || TYPE_STYLE.homeroom;
                  const Icon = s.Icon;
                  if (it.type === "break") {
                    return (
                      <div key={idx} className="flex items-center gap-2 px-2 py-1.5 text-xs text-slate-400">
                        <Coffee className="w-3 h-3" /> {it.title} · {fmt(it.start)}–{fmt(it.end)}
                      </div>
                    );
                  }
                  return (
                    <div key={idx} className={`flex gap-3 rounded-xl border p-3 ${s.bg} ${s.border}`}>
                      <div className={`w-9 h-9 shrink-0 rounded-lg bg-white flex items-center justify-center ${s.chip}`}>
                        <Icon className="w-4 h-4" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="font-semibold text-slate-900 text-sm leading-tight">{it.title}</p>
                        <p className={`text-xs font-medium mt-0.5 flex items-center gap-1 ${s.chip}`}>
                          <Clock className="w-3 h-3" /> {fmt(it.start)} – {fmt(it.end)}
                        </p>
                        {it.teacher && (
                          <p className="text-xs text-slate-400 mt-0.5 flex items-center gap-1">
                            <User className="w-3 h-3" /> {it.teacher}
                          </p>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </motion.div>
        );
      })}
    </div>
  );
}
